import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dots } from "./ui";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

export type CartItem = { productId: string; blendId?: string | null; name: string; code: string; qty: number; sizeMl: number; unitPrice: number };

export function addToCart(item: CartItem) {
  const cart: CartItem[] = JSON.parse(sessionStorage.getItem("order_cart") || "[]");
  const found = cart.find((c) => c.productId === item.productId);
  if (found) found.qty += 1;
  else cart.push(item);
  sessionStorage.setItem("order_cart", JSON.stringify(cart));
}

export default function CatalogPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/catalog`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(setRows).catch((e) => setErr(String(e.message || e))).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.name_ar, r.family, r.family_ar, r.gender, r.gender_ar, r.dna, r.code, r.catalog_code]
      .map((x) => String(x || "").toLowerCase()).join(" ").includes(q));
  }, [rows, search]);

  if (loading) return <div className="bl-sub py-8 text-center" data-testid="catalog-loading">جارٍ التحميل…</div>;
  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="catalog-error">{err}</div>;

  return (
    <div data-testid="catalog-page">
      <div className="flex gap-2 mb-3 items-center flex-wrap">
        <input className="bl-input" style={{ maxWidth: 420 }} placeholder="ابحث: اسم / عربي / عائلة / نوع / نوتة / كود…"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalog-search-input" />
        <span className="bl-sub" data-testid="catalog-count">{filtered.length} منتج</span>
        <button className="bl-btn primary mr-auto" onClick={() => nav("/pureh/blend/orders")} data-testid="catalog-goto-orders">🧾 الطلبات</button>
      </div>
      <div className="bl-grid">
        {filtered.map((b) => (
          <div key={b.id} className="bl-card" data-testid={`catalog-card-${b.catalog_code || b.code}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-extrabold bl-en">{b.name}</span>
              <span className="bl-sub">{b.name_ar}</span>
              {b.role_tag && <span className={`bl-pill ${b.kind === "original" ? "" : ""}`}>{b.role_tag === "ORIGINAL" ? "أصلي 🔒" : b.role_tag}</span>}
              {b.archived && <span className="bl-pill danger">مؤرشفة</span>}
            </div>
            <div className="bl-sub mt-1">
              {(b.family_ar || b.family) && <span className="bl-pill">{b.family_ar || b.family}</span>}{" "}
              {(b.gender_ar || b.gender) && <span className="bl-pill">{b.gender_ar || b.gender}</span>}{" "}
              <span className="bl-en">{b.productNumber || b.code}</span>
            </div>
            {b.dna && <div className="bl-sub mt-1" style={{ fontSize: 11 }}>{String(b.dna).slice(0, 90)}</div>}
            {b.performance && (
              <div className="flex gap-3 mt-2 items-center">
                <span className="bl-sub" style={{ fontSize: 10 }}>ثبات</span><Dots v={b.performance.longevity} />
                <span className="bl-sub" style={{ fontSize: 10 }}>فوحان</span><Dots v={b.performance.projection} />
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button className="bl-btn primary" style={{ padding: "6px 14px" }} data-testid={`catalog-add-order-${b.catalog_code || b.code}`}
                onClick={() => { addToCart({ productId: b.id, blendId: b.blendId, name: b.name, code: b.code, qty: 1, sizeMl: 50, unitPrice: 0 }); toast.success(`${b.name} اتضاف للطلب`); }}>
                🛒 أضف للطلب
              </button>
              <button className="bl-btn ghost" style={{ padding: "6px 14px" }}
                onClick={() => nav(b.blendId ? `/pureh/blend/detail/${b.blendId}` : "/pureh/product-master")}
                data-testid={`catalog-detail-${b.catalog_code || b.code}`}>الكارت</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
